'use client';

import { PageSkeleton } from '@/app/[locale]/components/PageSkeleton';
import { buildMultiContext } from '@/config/launchDarkly';
import { useOfferToolUser } from '@/providers/OfferToolUserProvider/OfferToolUserProvider';
import { datadogRum } from '@datadog/browser-rum';
import { LDProvider, ProviderConfig } from 'launchdarkly-react-client-sdk';
import { ReactNode, useMemo } from 'react';

export const LaunchDarklyProvider = ({
  children,
  config,
}: {
  children: ReactNode;
  config: ProviderConfig;
}) => {
  const { user, isLoading: isUserLoading } = useOfferToolUser();

  // Build multi-context from user data
  // IMPORTANT: All hooks must be called before any conditional returns
  const ldConfig = useMemo(() => {
    if (!user) {
      return config;
    }

    const multiContext = buildMultiContext(user);

    return {
      ...config,
      context: multiContext,
    };
  }, [user, config]);

  const providerKey = useMemo(() => {
    if (!user) {
      return 'anonymous';
    }
    return `${user.id}-${user.tenant?.id ?? 'no-tenant'}`;
  }, [user]);

  // Wait for user data before initializing LaunchDarkly
  // This check happens AFTER all hooks are called
  if (isUserLoading) {
    return <PageSkeleton />;
  }

  return (
    <LDProvider
      {...ldConfig}
      key={providerKey}
      options={{
        inspectors: [
          {
            type: 'flag-used',
            name: 'dd-inspector',
            method: (key, detail) => {
              datadogRum.addFeatureFlagEvaluation(key, detail.value);
            },
          },
        ],
      }}
    >
      {children}
    </LDProvider>
  );
};
